import { useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import HotelSearchbar from "./HotelSearchbar";
import HotelCard from "./HotelCard";
import HotelFilterbar from "./HotelFilterbar";

const defaultLatLng = {
  latitude: 15.2993,
  longitude: 74.124,
  countryCode: "IN",
};

const HotelResults = () => {
  const location = useLocation();
  const {
    city,
    checkInDate,
    checkOutDate,
    rooms = 1,
    guests = 2,
    results: initialResults = [],
  } = location.state || {};

  // Normalize API results to always be an array of hotels
  const normalizeResults = (results: any) => {
    if (!results) return [];
    // If results is an array, return as is
    if (Array.isArray(results)) return results;
    // If results is an object, wrap in array
    return [results];
  };

  // Extract hotels from API response structure
  const extractHotels = (results: any) => {
    // If results is an array, return as is
    if (Array.isArray(results)) return results;
    // If results is an object with HotelAvailInfo array, extract it
    if (
      results &&
      results.HotelAvailInfo &&
      Array.isArray(results.HotelAvailInfo)
    ) {
      return results.HotelAvailInfo;
    }
    // If results is a single hotel object
    return normalizeResults(results);
  };

  // FilterBar state
  const [selectedRatings, setSelectedRatings] = useState<string[]>([]);
  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<string[]>(
    [],
  );
  const [price, setPrice] = useState(26000);
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);

  // Results state
  const [results, setResults] = useState<any[]>(extractHotels(initialResults));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setResults(extractHotels(initialResults));
  }, [initialResults]);

  // Filtering logic
  const filteredHotels = useMemo(() => {
    return results.filter((hotel: any) => {
      // Filter by rating
      if (selectedRatings.length > 0) {
        const rating =
          Number(hotel?.HotelInfo?.SabreRating) ||
          Number(hotel?.HotelInfo?.Rating) ||
          0;
        // Accept "3+" as >= 3
        if (
          selectedRatings.some((r) =>
            r.endsWith("+")
              ? rating < Number(r.replace("+", ""))
              : rating !== Number(r),
          )
        ) {
          return false;
        }
      }

      // Filter by price (match HotelCard price logic)
      const hotelPrice = Number(
        hotel?.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]
          ?.AmountAfterTax ||
          hotel?.HotelRateInfo?.Total?.AmountAfterTax ||
          hotel?.HotelRateInfo?.RateRanges?.RateRange?.[0]?.MinRate ||
          0,
      );
      if (price && hotelPrice > price) return false;

      // Filter by facilities (if implemented)
      if (
        selectedFacilities.length > 0 &&
        hotel?.HotelInfo?.Amenities?.Amenity
      ) {
        const hotelAmenities = hotel.HotelInfo.Amenities.Amenity.map((a: any) =>
          (a.Description || a.Code || "").toLowerCase(),
        );
        if (
          !selectedFacilities.every((facility) =>
            hotelAmenities.includes(facility.toLowerCase()),
          )
        ) {
          return false;
        }
      }

      // Filter by property type (if implemented)
      if (selectedPropertyTypes.length > 0) {
        const propertyType =
          hotel?.HotelInfo?.PropertyType?.toLowerCase() || "";

        // Extract all property quality descriptions (lowercased)
        const propertyQualities =
          hotel?.HotelInfo?.PropertyQualityInfo?.PropertyQuality?.map(
            (q: any) => (q.Description || "").toLowerCase(),
          ) || [];

        // Check if any selected type matches either propertyType or propertyQualities
        const matchesType = selectedPropertyTypes.some(
          (type) =>
            propertyType === type.toLowerCase() ||
            propertyQualities.includes(type.toLowerCase()),
        );

        if (!matchesType) {
          return false;
        }
      }

      // Filter by reservation policies (if implemented)
      // Add your logic here if needed

      return true;
    });
  }, [
    results,
    selectedRatings,
    price,
    selectedFacilities,
    selectedPropertyTypes,
    selectedPolicies,
  ]);

  return (
    <div className="max-w-screen-xl mx-auto p-6">
      <HotelSearchbar
        city={city}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        rooms={rooms}
        guests={guests}
      />

      <div className="flex gap-6 py-6">
        <div className="w-[280px] shrink-0">
          <HotelFilterbar
            selectedRatings={selectedRatings}
            setSelectedRatings={setSelectedRatings}
            selectedFacilities={selectedFacilities}
            setSelectedFacilities={setSelectedFacilities}
            selectedPropertyTypes={selectedPropertyTypes}
            setSelectedPropertyTypes={setSelectedPropertyTypes}
            price={price}
            setPrice={setPrice}
            selectedPolicies={selectedPolicies}
            setSelectedPolicies={setSelectedPolicies}
          />
        </div>

        <div className="flex-1 space-y-6">
          {loading ? (
            <p className="text-center text-gray-500">Loading hotels...</p>
          ) : filteredHotels.length === 0 ? (
            <p className="text-center text-gray-500">No hotels found.</p>
          ) : (
            filteredHotels.map((hotel: any, idx: number) => (
              <HotelCard
                key={hotel.HotelInfo?.HotelCode || idx}
                image={
                  hotel.HotelImageInfo?.ImageItem?.Image?.Url ||
                  hotel.HotelInfo?.ImageUrl ||
                  "/assets/hotel-placeholder.jpg"
                }
                name={
                  hotel.HotelInfo?.HotelName || hotel.HotelInfo?.Name || "Hotel"
                }
                location={
                  hotel.HotelInfo?.LocationInfo?.Address?.AddressLine1 ||
                  hotel.HotelInfo?.LocationDescription ||
                  ""
                }
                rating={
                  hotel.HotelInfo?.SabreRating || hotel.HotelInfo?.Rating || 0
                }
                roomType={
                  hotel.HotelRateInfo?.Rooms?.Room?.[0]?.RoomType ||
                  hotel.RoomType ||
                  "Standard Room"
                }
                beds={
                  hotel.HotelRateInfo?.Rooms?.Room?.[0]?.BedTypeOptions
                    ?.BedTypes?.[0]?.BedType?.length > 0
                    ? hotel.HotelRateInfo?.Rooms?.Room?.[0]?.BedTypeOptions?.BedTypes?.[0]?.BedType.map(
                        (bed: any) =>
                          `${bed.Count} ${bed.Description}${
                            bed.Count > 1 ? " beds" : " bed"
                          }`,
                      ).join(" or ")
                    : hotel.HotelRateInfo?.Rooms?.Room?.[0]?.BedsDescription ||
                      hotel.BedsDescription ||
                      "1 double or 2 single"
                }
                amenities={
                  Array.isArray(hotel.HotelInfo?.Amenities?.Amenity)
                    ? hotel.HotelInfo.Amenities.Amenity.map(
                        (a: any) => a.Description || a.Code || "",
                      )
                    : []
                }
                price={
                  hotel.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]
                    ?.AmountAfterTax ||
                  hotel.HotelRateInfo?.Total?.AmountAfterTax ||
                  0
                }
                currency={
                  hotel.HotelRateInfo?.RateInfos?.ConvertedRateInfo?.[0]
                    ?.CurrencyCode ||
                  hotel.HotelRateInfo?.Total?.CurrencyCode ||
                  "INR"
                }
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelResults;
